import * as THREE from 'three';
import { Assembly, DEFAULT_SNAP_METRES, type AssemblyResult } from '../engine/assembly.js';
import { layOut, shuffled, type Footprint } from '../engine/assembly-layout.js';
import type { Hands } from './hands.js';
import type { BuiltObject } from './scene.js';

/**
 * An assembly, in the room: loose parts on a tray, faint outlines where they
 * belong, and hands or a pointer to move one to the other (ADR-025).
 *
 * The rules -- which part is home, when it is done, what the teacher gets --
 * are `engine/assembly.ts` and are tested there. This is only the part that
 * needs a headset: where things lie, what follows a hand, and what a child sees
 * when a bone clicks in.
 *
 * Everything happens in the lesson root's own space. A student who has shrunk
 * the skeleton onto a desk (`Placeable`) gets a desk-sized pile and desk-sized
 * outlines without any of this knowing the scale changed.
 *
 * Two ways to hold a part, deliberately different:
 *
 * - **A hand pinches at the part itself.** Near-field, not a laser: a child who
 *   can pull a femur across the room has learned nothing about where it is.
 * - **A controller or the desktop mouse points at it** and carries it along the
 *   ray. Controllers are not replaced by hands (a cast, a sleeve, a dark room),
 *   and the laptop preview has to be able to do the lesson too (CLAUDE.md).
 */

/** Where the loose parts lie, in the root's space: roughly table height. */
const TRAY_HEIGHT = 0.85;
/** How far in front of the skeleton the pile starts, towards the student. */
const TRAY_START = 0.32;
/** How wide the pile may get before it wraps to another row. */
const TRAY_WIDTH = 1.1;
const TRAY_GAP = 0.05;

/**
 * How close a pinch has to be to a part to take hold of it, in metres from the
 * part's bounding box. Close enough to mean "that one", loose enough that a
 * child's hand tracking wobble does not miss.
 */
const HAND_REACH = 0.045;

/** Long thin parts lie down on the tray; a skull or a pelvis sits up. */
const LIES_DOWN_WHEN = 1.6;

const FLASH_SECONDS = 0.45;
const RETURN_SECONDS = 0.28;

type Part = {
  id: string;
  object: BuiltObject;
  home: THREE.Vector3;
  /**
   * Where it waits, relative to the tray -- so when a student moves the tray
   * the parts lying on it move with it -- and how it lies there.
   */
  loose: THREE.Vector3;
  lying: THREE.Quaternion;
  ghost: THREE.Object3D;
  /**
   * The meshes a ray can find, and only those. A part's group also holds its
   * floating label, a Sprite, and three.js cannot raycast a sprite without a
   * camera -- a recursive raycast over the group threw inside the pointer
   * handler, so clicking a bone crashed the press and the drag with it.
   */
  meshes: THREE.Mesh[];
  /** Seconds left on the "it clicked in" glow. */
  flash: number;
  /** A part sliding back to the tray after a wrong drop. */
  returning?: { from: THREE.Vector3; fromTurn: THREE.Quaternion; t: number } | undefined;
};

type Hold =
  | { by: 'hand'; hand: number; part: Part; offset: THREE.Vector3 }
  | { by: 'ray'; part: Part; distance: number; offset: THREE.Vector3 };

const UPRIGHT = new THREE.Quaternion();

export class AssemblyBench {
  readonly #root: THREE.Object3D;
  readonly #assembly: Assembly;
  readonly #parts = new Map<string, Part>();
  /**
   * The tray and everything lying on it. A group of its own, inside the
   * skeleton's space, so a student can put it somewhere else (Placeable,
   * locked by default) while it still shares the skeleton's size.
   */
  readonly tray = new THREE.Group();
  readonly #slab: THREE.Mesh;
  /**
   * A rail all round the tray, like a trolley's handle: what a student takes
   * hold of to move it. Grabbing the tray anywhere meant grabbing among the
   * bones; the rail is the one place that means "the tray".
   */
  #rail!: THREE.Mesh<THREE.TubeGeometry, THREE.MeshStandardMaterial>;
  #railPath: THREE.Vector3[] = [];
  readonly #ghostMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.13,
    depthWrite: false,
  });
  readonly #raycaster = new THREE.Raycaster();

  #holds: Hold[] = [];
  #active = false;
  readonly #snap: number;
  #onPlaced: ((placed: number, total: number) => void) | undefined;
  #onDone: ((result: AssemblyResult) => void) | undefined;

  constructor(
    root: THREE.Object3D,
    objects: ReadonlyMap<string, BuiltObject>,
    options: { parts: readonly string[]; snapMetres?: number | undefined; seed: string },
  ) {
    this.#root = root;
    this.#snap = options.snapMetres ?? DEFAULT_SNAP_METRES;
    root.updateMatrixWorld(true);

    const footprints: Footprint[] = [];
    const lyingOf = new Map<string, THREE.Quaternion>();
    const heightOf = new Map<string, number>();

    for (const id of options.parts) {
      const object = objects.get(id);
      if (!object) continue;

      const size = localSize(object.group);
      const lies = size.y > LIES_DOWN_WHEN * Math.max(size.x, size.z);
      // Lying down along the tray, pointing at the student: a long bone
      // standing on end reads as already placed, not as waiting to be.
      const lying = lies
        ? new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2)
        : new THREE.Quaternion();

      lyingOf.set(id, lying);
      heightOf.set(id, lies ? size.z : size.y);
      footprints.push({ id, width: size.x, depth: lies ? size.y : size.z });

      const ghost = object.group.clone(true);
      ghost.traverse((child) => {
        if (child instanceof THREE.Sprite) child.visible = false;
        if (child instanceof THREE.Mesh) {
          child.material = this.#ghostMaterial;
          // Not something a ray should find: the outline is a hint, not a part.
          child.raycast = () => {};
        }
      });
      ghost.visible = false;
      root.add(ghost);

      const meshes: THREE.Mesh[] = [];
      object.group.traverse((child) => {
        if (child instanceof THREE.Mesh) meshes.push(child as THREE.Mesh);
      });

      this.#parts.set(id, {
        id,
        object,
        home: object.group.position.clone(),
        loose: new THREE.Vector3(),
        lying,
        ghost,
        meshes,
        flash: 0,
      });
    }

    const spots = layOut(shuffled(footprints, options.seed), {
      maxWidth: TRAY_WIDTH,
      gap: TRAY_GAP,
      startZ: TRAY_START,
    });
    for (const part of this.#parts.values()) {
      const spot = spots.get(part.id)!;
      part.loose.set(spot.x, TRAY_HEIGHT + (heightOf.get(part.id) ?? 0) / 2, spot.z);
    }

    // Something to lie on. Loose bones floating at table height in a real room
    // read as a mess; the same bones on a pale tray read as a set of parts.
    this.#slab = trayUnder(
      footprints.map((footprint) => ({ ...footprint, at: this.#parts.get(footprint.id)!.loose })),
    );
    // The group sits where the slab's centre is; the slab and every waiting
    // part are then kept relative to it.
    this.tray.position.copy(this.#slab.position);
    this.#slab.position.set(0, 0, 0);
    for (const part of this.#parts.values()) part.loose.sub(this.tray.position);
    this.tray.add(this.#slab);
    this.#rail = this.#makeRail();
    this.tray.add(this.#rail);
    this.tray.visible = false;
    root.add(this.tray);

    this.#assembly = new Assembly({
      parts: [...this.#parts.values()].map((part) => ({
        id: part.id,
        home: [part.home.x, part.home.y, part.home.z],
      })),
      ...(options.snapMetres === undefined ? {} : { snapMetres: options.snapMetres }),
    });
  }

  /** Told how many are in, so the lectern can count down. */
  onPlaced(handler: (placed: number, total: number) => void): void {
    this.#onPlaced = handler;
  }

  onDone(handler: (result: AssemblyResult) => void): void {
    this.#onDone = handler;
  }

  get placed(): number {
    return this.#parts.size - this.#assembly.remaining;
  }

  get total(): number {
    return this.#parts.size;
  }

  /**
   * The step has begun, or been come back to. Parts already placed stay put;
   * the rest go to the tray.
   */
  enter(): void {
    if (this.#active || this.#assembly.done) return;
    this.#active = true;
    this.tray.visible = true;

    for (const part of this.#parts.values()) {
      const placed = this.#assembly.isPlaced(part.id);
      part.ghost.visible = !placed;
      if (placed) this.#setAt(part, part.home, UPRIGHT);
      else this.#setAt(part, this.#looseOf(part), part.lying);
    }
  }

  /**
   * Any other step, or this one finished: the skeleton stands whole. A class
   * walking round it on the next step must not be walking round a pile.
   */
  leave(): void {
    if (!this.#active && !this.#assembly.done) {
      for (const part of this.#parts.values()) this.#setAt(part, part.home, UPRIGHT);
      return;
    }
    this.#active = false;
    this.#holds = [];
    this.tray.visible = false;
    for (const part of this.#parts.values()) {
      part.ghost.visible = false;
      part.returning = undefined;
      part.object.label.visible = false;
      this.#setAt(part, part.home, UPRIGHT);
    }
  }

  /**
   * One frame. Returns the hands it is using, so `Placeable` does not also
   * pick the whole skeleton up with them -- carrying a bone and carrying the
   * body are different intentions and must not happen together.
   */
  update(dt: number, hands: Hands, busy: ReadonlySet<number> = new Set()): ReadonlySet<number> {
    const used = new Set<number>();
    this.#animate(dt);
    if (!this.#active) return used;

    for (const [index, hand] of hands.states.entries()) {
      // A hand holding a handle -- the tray's rail, the skeleton's -- is not
      // reaching for a bone.
      if (busy.has(index)) continue;
      const held = this.#holds.find((hold) => hold.by === 'hand' && hold.hand === index);

      if (held) {
        if (!hand.tracked || hand.justReleased || !hand.pinching) {
          this.#release(held);
        } else {
          this.#carryTo(held.part, hand.pinchPoint, held.offset);
          used.add(index);
        }
        continue;
      }

      if (hand.tracked && hand.justPinched) {
        const part = this.#nearestTo(hand.pinchPoint);
        if (part) {
          const offset = this.#pickUp(part, hand.pinchPoint);
          this.#holds.push({ by: 'hand', hand: index, part, offset });
          used.add(index);
        }
      }
    }
    return used;
  }

  /**
   * A controller or the mouse pressed along this ray. True when it took hold
   * of a part, so the press is not also a select.
   */
  grabWithRay(ray: THREE.Ray): boolean {
    if (!this.#active) return false;

    this.#raycaster.set(ray.origin, ray.direction);
    const free = [...this.#parts.values()].filter((part) => !this.#isHeld(part));
    const hit = this.#raycaster.intersectObjects(
      free.flatMap((part) => part.meshes),
      false,
    )[0];
    if (!hit) return false;

    const part = free.find((candidate) => candidate.meshes.includes(hit.object as THREE.Mesh));
    if (!part) return false;

    const offset = this.#pickUp(part, hit.point);
    this.#holds.push({ by: 'ray', part, distance: hit.distance, offset });
    return true;
  }

  /**
   * Carries a part along a ray -- except when the ray is aimed at the part's
   * own outline, when it is shown there instead.
   *
   * A part carried at a fixed distance along a ray can never reach its home:
   * the tray is nearer the student than the skeleton, so the depth is always
   * wrong and a controller or a mouse could not finish the lesson at all. What
   * a ray can do well is *aim*. So the question is whether it passes close
   * enough to the part's home, and if it does the part previews there -- which
   * is exactly where letting go will put it, so what the student sees is what
   * will happen.
   */
  moveRay(ray: THREE.Ray): void {
    const hold = this.#holds.find((candidate) => candidate.by === 'ray');
    if (hold?.by !== 'ray') return;

    const part = hold.part;
    const home = this.#root.localToWorld(part.home.clone());
    const aimed = this.#root.worldToLocal(ray.closestPointToPoint(home, new THREE.Vector3()));

    if (aimed.distanceTo(part.home) <= this.#snap) {
      part.object.group.position.copy(part.home);
      return;
    }
    this.#carryTo(part, ray.at(hold.distance, new THREE.Vector3()), hold.offset);
  }

  endRay(): void {
    const hold = this.#holds.find((candidate) => candidate.by === 'ray');
    if (hold) this.#release(hold);
  }

  /** Whether its step is being built right now. */
  get active(): boolean {
    return this.#active;
  }

  /**
   * The skeleton's own outline, in world space: every part at home, which is
   * where the faint outlines always are. Not the tray -- it lives inside the
   * skeleton's space, and a hand on the tray must not count as a hand on the
   * body.
   */
  skeletonBox(): THREE.Box3 {
    const box = new THREE.Box3();
    for (const part of this.#parts.values()) box.expandByObject(part.ghost);
    return box;
  }

  /**
   * Where the tray's padlock goes, in the tray's own space: just off its
   * right-hand corner on the skeleton's side. Not the corner nearest the
   * student, which from standing height is under their own line of sight --
   * which is where this first went.
   */
  get lockSpot(): THREE.Vector3 {
    const size = new THREE.Box3().setFromObject(this.#slab).getSize(new THREE.Vector3());
    const scale = this.tray.getWorldScale(new THREE.Vector3()).x || 1;
    return new THREE.Vector3(size.x / scale / 2 + 0.07, 0.06, -size.z / scale / 2);
  }

  /**
   * The rail's path in the world: what a pinch or a pointer is measured
   * against, rather than the thin tube drawn along it.
   */
  railPath(): THREE.Vector3[] {
    return this.#railPath.map((point) => this.tray.localToWorld(point.clone()));
  }

  /**
   * Amber while the tray can be moved, the colour every movable thing has;
   * a quiet grey while it is locked, when the rail is just its edge.
   */
  setMovable(movable: boolean): void {
    this.#rail.material.color.setHex(movable ? 0xf59e0b : 0xb8bec8);
    this.#rail.material.emissive.setHex(movable ? 0x6b3f00 : 0x000000);
  }

  #makeRail(): THREE.Mesh<THREE.TubeGeometry, THREE.MeshStandardMaterial> {
    const size = new THREE.Box3().setFromObject(this.#slab).getSize(new THREE.Vector3());
    const scale = this.tray.getWorldScale(new THREE.Vector3()).x || 1;
    const halfW = size.x / scale / 2 + 0.012;
    const halfD = size.z / scale / 2 + 0.012;
    const corner = 0.045;
    const height = 0.035;

    // A rounded rectangle just outside the slab's edge, a few centimetres up.
    const points: THREE.Vector3[] = [];
    const corners: [number, number, number][] = [
      [halfW - corner, halfD - corner, 0],
      [-(halfW - corner), halfD - corner, Math.PI / 2],
      [-(halfW - corner), -(halfD - corner), Math.PI],
      [halfW - corner, -(halfD - corner), Math.PI * 1.5],
    ];
    for (const [cx, cz, start] of corners) {
      for (let i = 0; i <= 6; i++) {
        const a = start + (i / 6) * (Math.PI / 2);
        points.push(
          new THREE.Vector3(cx + Math.cos(a) * corner, height, cz + Math.sin(a) * corner),
        );
      }
    }
    this.#railPath = [...points, points[0]!.clone()];

    const rail = new THREE.Mesh(
      new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points, true), 96, 0.011, 8, true),
      new THREE.MeshStandardMaterial({ color: 0xb8bec8, roughness: 0.4, metalness: 0.2 }),
    );
    rail.userData['rail'] = true;
    return rail;
  }

  trayBox(): THREE.Box3 {
    return new THREE.Box3().setFromObject(this.#slab).expandByVector(new THREE.Vector3(0, 0.06, 0));
  }

  get holdingWithRay(): boolean {
    return this.#holds.some((hold) => hold.by === 'ray');
  }

  dispose(): void {
    for (const part of this.#parts.values()) part.ghost.removeFromParent();
    this.tray.removeFromParent();
    this.#slab.geometry.dispose();
    (this.#slab.material as THREE.Material).dispose();
    this.#rail.geometry.dispose();
    this.#rail.material.dispose();
    this.#ghostMaterial.dispose();
  }

  // --- holding ---------------------------------------------------------------

  /**
   * Takes a part in hand. It stands up as it is lifted, so it can be matched
   * against its outline by shape, and its name appears -- which is half of
   * what the step is teaching.
   */
  #pickUp(part: Part, at: THREE.Vector3): THREE.Vector3 {
    if (this.#assembly.isPlaced(part.id)) {
      // Taking a placed bone back out is allowed: a child checking something
      // should not be punished for the curiosity a lesson is trying to provoke.
      this.#assembly.take(part.id);
      part.ghost.visible = true;
      this.#onPlaced?.(this.placed, this.total);
    }
    part.returning = undefined;
    part.object.group.quaternion.copy(UPRIGHT);
    part.object.label.visible = true;

    // Kept, so the part does not jump to centre itself on the fingertips.
    const world = part.object.group.getWorldPosition(new THREE.Vector3());
    return world.sub(at);
  }

  #carryTo(part: Part, worldPoint: THREE.Vector3, offset: THREE.Vector3): void {
    const target = worldPoint.clone().add(offset);
    part.object.group.position.copy(this.#root.worldToLocal(target));
  }

  #release(hold: Hold): void {
    this.#holds = this.#holds.filter((candidate) => candidate !== hold);
    const part = hold.part;
    part.object.label.visible = false;

    const at = part.object.group.position;
    const outcome = this.#assembly.place(part.id, [at.x, at.y, at.z]);

    if (outcome === 'placed') {
      this.#setAt(part, part.home, UPRIGHT);
      part.ghost.visible = false;
      part.flash = FLASH_SECONDS;
      this.#onPlaced?.(this.placed, this.total);

      if (this.#assembly.done) {
        const result = this.#assembly.result();
        this.#active = false;
        this.tray.visible = false;
        this.#onDone?.(result);
      }
      return;
    }

    // Not an error and not a scolding: it goes back to the tray to be tried
    // again. Slid rather than snapped, so the child sees where it went.
    part.returning = {
      from: part.object.group.position.clone(),
      fromTurn: part.object.group.quaternion.clone(),
      t: 0,
    };
  }

  // --- finding ---------------------------------------------------------------

  #nearestTo(point: THREE.Vector3): Part | undefined {
    let best: Part | undefined;
    let bestDistance = HAND_REACH;
    for (const part of this.#parts.values()) {
      if (this.#isHeld(part)) continue;
      // The box, not the centre: a femur is pinched at its end as often as its
      // middle, and the end is a quarter of a metre from the centre.
      const box = new THREE.Box3().setFromObject(part.object.group);
      const distance = box.distanceToPoint(point);
      if (distance <= bestDistance) {
        bestDistance = distance;
        best = part;
      }
    }
    return best;
  }

  #isHeld(part: Part): boolean {
    return this.#holds.some((hold) => hold.part === part);
  }

  // --- showing ---------------------------------------------------------------

  #setAt(part: Part, position: THREE.Vector3, turn: THREE.Quaternion): void {
    part.object.group.position.copy(position);
    part.object.group.quaternion.copy(turn);
  }

  /** Where a waiting part lies right now, in the skeleton's space. */
  #looseOf(part: Part): THREE.Vector3 {
    return this.tray.position.clone().add(part.loose);
  }

  #animate(dt: number): void {
    for (const part of this.#parts.values()) {
      // Parts waiting on the tray go wherever the tray has been put.
      const waiting =
        this.#active &&
        !this.#assembly.isPlaced(part.id) &&
        !this.#isHeld(part) &&
        part.returning === undefined;
      if (waiting) part.object.group.position.copy(this.#looseOf(part));

      if (part.returning) {
        part.returning.t = Math.min(1, part.returning.t + dt / RETURN_SECONDS);
        const eased = 1 - (1 - part.returning.t) ** 3;
        part.object.group.position.lerpVectors(part.returning.from, this.#looseOf(part), eased);
        part.object.group.quaternion.slerpQuaternions(part.returning.fromTurn, part.lying, eased);
        if (part.returning.t >= 1) part.returning = undefined;
      }

      if (part.flash > 0) {
        part.flash = Math.max(0, part.flash - dt);
        glow(part.object.group, part.flash / FLASH_SECONDS);
      }
    }
  }
}

/**
 * A part's size in its own space, ignoring its label and whatever the lesson
 * root has done to it -- a shrunk skeleton still lays its bones out by their
 * own proportions.
 */
function localSize(group: THREE.Object3D): THREE.Vector3 {
  group.updateMatrixWorld(true);
  const toGroup = group.matrixWorld.clone().invert();
  const box = new THREE.Box3();

  group.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const geometry = child.geometry as THREE.BufferGeometry;
    if (!geometry.boundingBox) geometry.computeBoundingBox();
    const piece = geometry.boundingBox!.clone();
    piece.applyMatrix4(new THREE.Matrix4().multiplyMatrices(toGroup, child.matrixWorld));
    box.union(piece);
  });
  return box.getSize(new THREE.Vector3());
}

/** A pale, thin slab under the pile. */
function trayUnder(parts: readonly (Footprint & { at: THREE.Vector3 })[]): THREE.Mesh {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const part of parts) {
    minX = Math.min(minX, part.at.x - part.width / 2);
    maxX = Math.max(maxX, part.at.x + part.width / 2);
    minZ = Math.min(minZ, part.at.z - part.depth / 2);
    maxZ = Math.max(maxZ, part.at.z + part.depth / 2);
  }
  const margin = 0.06;
  const tray = new THREE.Mesh(
    new THREE.BoxGeometry(maxX - minX + margin * 2, 0.012, maxZ - minZ + margin * 2),
    new THREE.MeshStandardMaterial({
      color: 0xd9dde4,
      roughness: 0.9,
      transparent: true,
      opacity: 0.55,
    }),
  );
  tray.position.set((minX + maxX) / 2, TRAY_HEIGHT - 0.006, (minZ + maxZ) / 2);
  tray.raycast = () => {};
  return tray;
}

/**
 * The "it clicked in" glow, fading with `amount` from 1 to 0. A shape is one
 * mesh with a material per opacity (shapes.ts), so a mesh may have several.
 */
export function glow(group: THREE.Object3D, amount: number): void {
  group.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    const materials = child.material as THREE.Material | THREE.Material[];
    for (const material of Array.isArray(materials) ? materials : [materials]) {
      if (material instanceof THREE.MeshStandardMaterial) {
        material.emissive.setHex(0x3fbf6f);
        material.emissiveIntensity = amount * 0.8;
      }
    }
  });
}
